import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import type { MenuItemDto } from "core/entities";
import { useAddUserLog } from "core/hooks/useUserLog";
import { NotificationDot } from "features/Notifications/NotificationDot";
import { type HTMLAttributeAnchorTarget, memo, useState } from "react";
import { TransitionGroup } from "react-transition-group";
import { CustomLink } from "../CustomLink";

export interface NavCategoryLinksProps {
  id: string;
  href?: string | undefined;
  target?: HTMLAttributeAnchorTarget | undefined;
  displayName: string;
  submenus: MenuItemDto[];
  numberOfInitiallyVisibleLinks: number;
}

export const NavCategoryLinks = memo(
  ({
    id,
    displayName,
    href,
    target,
    submenus,
    numberOfInitiallyVisibleLinks,
  }: NavCategoryLinksProps) => {
    const addLog = useAddUserLog();
    const [isExpanded, setIsExpanded] = useState(false);
    const visibleSubmenus = isExpanded
      ? submenus
      : submenus.slice(0, numberOfInitiallyVisibleLinks);
    const isShowAllButtonVisible =
      !isExpanded && submenus.length > numberOfInitiallyVisibleLinks;
    return (
      <List sx={{ width: "24ch" }}>
        <ListItem disableGutters>
          {href ? (
            <CustomLink href={href} target={target} variant="button">
              {displayName}{" "}
              <NotificationDot translate={{ x: 0, y: 0 }} mode="self" id={id} />
            </CustomLink>
          ) : (
            <Typography variant="button">{displayName}</Typography>
          )}
        </ListItem>
        <Divider />
        <TransitionGroup>
          {visibleSubmenus.map(
            ({
              id: linkId,
              url: linkURL,
              displayName: linkDisplayName,
              target: linkTarget,
            }) =>
              !linkURL ? null : (
                <Collapse key={linkId}>
                  <ListItem disableGutters>
                    <CustomLink
                      onClick={async () => {
                        if (linkTarget !== "_blank") {
                          return;
                        }
                        await addLog("External", linkURL);
                      }}
                      href={linkURL}
                      underline="none"
                      color="primary.dark"
                      target={linkTarget ?? undefined}
                    >
                      {linkDisplayName}
                      <NotificationDot mode="self" id={linkId!} />
                    </CustomLink>
                  </ListItem>
                </Collapse>
              )
          )}
        </TransitionGroup>
        <Collapse in={isShowAllButtonVisible}>
          <ListItem
            disableGutters
            sx={{
              p: 0,
              my: 1,
            }}
          >
            <ListItemButton
              data-testid="see-all-button"
              onClick={() => {
                setIsExpanded(true);
              }}
              sx={{ justifyContent: "center" }}
            >
              <KeyboardDoubleArrowDownIcon color="primary" />
            </ListItemButton>
          </ListItem>
        </Collapse>
      </List>
    );
  }
);
NavCategoryLinks.displayName = "NavCategoryLinks";
